<template>
  <div>
    <v-card class="mb-4">
      <v-card-title class="d-flex align-center flex-wrap ga-3">
        <span>{{ $t(isLite ? 'taskSCurve.liteTitle' : 'taskSCurve.title') }}</span>
        <v-chip v-if="isLite" size="small" variant="tonal" color="warning">
          {{ $t('taskSCurve.liteChip') }}
          <v-tooltip activator="parent" location="bottom" max-width="320">
            {{ $t('taskSCurve.liteTooltip') }}
          </v-tooltip>
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div v-if="summary" class="text-body-2 text-medium-emphasis mb-2">
          <!-- PM lite: every task is 1 day, so the total is a task count and there is no planned figure -->
          <template v-if="isLite">
            {{ summary.totalManDays.toLocaleString() }} {{ $t('taskSCurve.tasksUnit') }} ·
            {{ summary.firstDate }} → {{ summary.lastDate }} ·
            {{ $t('taskSCurve.today') }}: {{ $t('taskSCurve.actual') }} {{ summary.actualToday.toFixed(2) }}%
          </template>
          <template v-else>
            {{ summary.totalManDays.toLocaleString(undefined, { maximumFractionDigits: 1 }) }} {{ $t('taskSCurve.manDays') }} ·
            {{ summary.firstDate }} → {{ summary.lastDate }} ·
            {{ $t('taskSCurve.today') }}: {{ $t('taskSCurve.planned') }} {{ summary.plannedToday.toFixed(2) }}% /
            {{ $t('taskSCurve.actual') }} {{ summary.actualToday.toFixed(2) }}%
            <span :class="summary.variance >= 0 ? 'text-success' : 'text-error'">
              ({{ summary.variance >= 0 ? '+' : '' }}{{ summary.variance.toFixed(2) }}%)
            </span>
          </template>
          <span v-if="isTaskSCurveFiltered"> · {{ $t('taskSCurve.filtered') }}</span>
        </div>
        <div v-else-if="dataLoaded && !loading" class="text-body-2 text-medium-emphasis mb-2">
          {{ $t(isLite ? 'taskSCurve.noProgress' : 'taskSCurve.noData') }}
        </div>
        <v-chart class="chart" :option="chartOption" autoresize />
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  DataZoomComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import { sCurveSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, isTaskSCurveFiltered, isTaskScopeReady, isTaskScopeRestricted, taskScope } from '@/utils/taskSCurveFilters';

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  DataZoomComponent
]);

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const chartColor = computed(() => store.getters.chartFontColor);

const chartOption = shallowRef({});
const summary = ref(null);
const loading = ref(false);

// 'sync' (planned dates on every task) or 'lite' (no dates; each task counts as 1 day, so
// only the actual curve exists). Decided from the data when the tables are built.
const scheduleMode = ref('sync');
const isLite = computed(() => scheduleMode.value === 'lite');

// The team filter lives on the page toolbar (taskSCurveFilters); this chart only reads it.

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  // COUNT(*) etc. come back as BigInt, so callers wrap numeric columns in Number()
  return result.toArray().map(row => row.toJSON());
};

const loadChartData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;

  scheduleMode.value = (await queryRows(`SELECT mode FROM schedule_mode`))[0]?.mode ?? 'sync';

  // Unfiltered: read the table built at load time. Filtered: run the same SQL with a
  // WHERE on s_curve_units — a few thousand rows, so it's still milliseconds.
  const sql = isTaskScopeRestricted.value
    ? sCurveSql(taskScope())
    : `SELECT * FROM s_curve ORDER BY date`;

  const rows = await queryRows(`
    SELECT CAST(date AS VARCHAR) AS date,
           plannedDeltaManDays, actualDeltaManDays,
           plannedManDays, actualManDays, totalManDays,
           targetProgressPct, actualProgressPct
    FROM (${sql}) q
    ORDER BY date
  `);
  if (version !== queryVersion) return; // a newer request superseded this one
  loading.value = false;
  updateChartOption(rows);
};

const num = v => (v === null || v === undefined ? null : Number(v));
const fmt = (v, d = 1) => (v === null ? '–' : v.toFixed(d));

const updateChartOption = (rows) => {
  if (!rows.length) {
    chartOption.value = {};
    summary.value = null;
    return;
  }

  const today = new Date().toISOString().slice(0, 10);
  const todayRow = [...rows].reverse().find(r => r.date <= today) ?? rows[0];
  const plannedToday = isLite.value ? null : (num(todayRow.targetProgressPct) ?? 0);
  const actualToday = num(todayRow.actualProgressPct) ?? 0;
  summary.value = {
    totalManDays: num(rows[0].totalManDays) ?? 0,
    firstDate: rows[0].date,
    lastDate: rows[rows.length - 1].date,
    plannedToday,
    actualToday,
    variance: plannedToday === null ? null : actualToday - plannedToday,
  };

  const plannedLabel = t('taskSCurve.planned');
  const actualLabel = t('taskSCurve.actual');
  const manDaysLabel = t('taskSCurve.manDays');
  const series = buildSeries(rows, today);

  chartOption.value = {
    animationDuration: 400,
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        const row = rows[params[0].dataIndex];
        const planned = num(row.targetProgressPct);
        const actual = num(row.actualProgressPct);
        if (isLite.value) {
          return `<b>${row.date}</b><br/>` +
            (actual === null
              ? `${actualLabel}: –`
              : `${actualLabel}: <b>${fmt(actual, 2)}%</b> (${fmt(num(row.actualManDays))} / ${fmt(num(row.totalManDays), 0)} ${t('taskSCurve.tasksUnit')})`);
        }
        return `<b>${row.date}</b><br/>` +
          `${plannedLabel}: <b>${fmt(planned, 2)}%</b> (${fmt(num(row.plannedManDays))} / ${fmt(num(row.totalManDays))} ${manDaysLabel})<br/>` +
          (actual === null
            ? `${actualLabel}: –`
            : `${actualLabel}: <b>${fmt(actual, 2)}%</b> (${fmt(num(row.actualManDays))} ${manDaysLabel})<br/>` +
              `${t('taskSCurve.variance')}: <b>${actual - planned >= 0 ? '+' : ''}${fmt(actual - planned, 2)}%</b>`);
      }
    },
    legend: { top: 0, textStyle: { color: chartColor.value } },
    grid: { left: 48, right: 24, top: 40, bottom: 60, containLabel: true },
    xAxis: {
      type: 'category',
      data: rows.map(r => r.date),
      axisLabel: { color: chartColor.value },
      boundaryGap: false
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { color: chartColor.value, formatter: '{value}%' },
      splitLine: { lineStyle: { opacity: 0.2 } }
    },
    dataZoom: [{ type: 'inside' }, { type: 'slider', bottom: 8, height: 18 }],
    series
  };
};

// Planned + Actual (sync), or Actual alone (PM lite). The Today marker rides on the first series.
const buildSeries = (rows, today) => {
  const planned = {
    name: t('taskSCurve.planned'),
    type: 'line',
    showSymbol: false,
    data: rows.map(r => num(r.targetProgressPct)),
    lineStyle: { width: 2, color: '#3B82F6' },
    itemStyle: { color: '#3B82F6' },
    areaStyle: { color: '#3B82F6', opacity: 0.06 }
  };
  const actual = {
    name: t('taskSCurve.actual'),
    type: 'line',
    showSymbol: false,
    connectNulls: false,
    data: rows.map(r => num(r.actualProgressPct)),
    lineStyle: { width: 2.5, color: '#10B981' },
    itemStyle: { color: '#10B981' }
  };
  const series = isLite.value ? [actual] : [planned, actual];
  series[0].markLine = {
    symbol: 'none',
    label: { formatter: t('taskSCurve.today'), color: chartColor.value },
    lineStyle: { type: 'dashed', color: '#F59E0B' },
    data: rows.some(r => r.date === today) ? [{ xAxis: today }] : []
  };
  return series;
};

onMounted(loadChartData);
watch(
  [dataLoaded, chartColor, taskScopeKey],
  loadChartData,
  { deep: true }
);
</script>

<style scoped>
.chart {
  height: 380px;
  width: 100%;
}
</style>
